// Single source of truth for AI SEO pricing. The service page, its Offer schema, the
// enquiry form and the SEO blogs all import from here, so a price change is one edit.
//
// Static files cannot import this and must be updated by hand when a number changes:
//   public/services/seo-services-malaysia/llms.txt, public/llms.txt,
//   public/llms-full.txt, public/blog/{seo-price-malaysia-2026,ai-seo-agent-malaysia,
//   ai-seo-vs-traditional-seo-malaysia}/llms.txt, docs/ai-seo-service-design.md,
//   public/singapore/ai-seo/llms.txt, public/singapore/llms.txt (SGD prices)
// Also hardcoded: the `description` inside each blog's defineBlogPost({...}). The
// blog index evaluates that block in isolation, so it cannot see MDX imports.
//
// Repriced 2026-09-23 (was RM699 / RM1,499, no Managed tier). RM899 clears the
// "below RM800 = automated AI spam" line Google's AI Overview draws for "seo package
// malaysia" while staying under the cheapest verified AI-inclusive competitor plan
// (RM945, 12-month lock-in). Managed at RM3,800 sits with the MY GEO mid-market.

export const SEO_ANNUAL_DISCOUNT = 0.15;

/** Annual price: 12 months less the discount, rounded down to the ringgit. */
export function seoAnnualPrice(monthly: number): number {
  return Math.floor(monthly * 12 * (1 - SEO_ANNUAL_DISCOUNT));
}

export const SEO_PLANS = {
  essential: {
    name: "AI SEO Essential",
    monthly: 899,
    keywordThemes: 10,
    aiQuestions: 10,
  },
  growth: {
    name: "AI SEO Growth",
    monthly: 1999,
    keywordThemes: 25,
    aiQuestions: 25,
  },
  managed: {
    name: "AI SEO Managed",
    // "From": scoped per client, so there is no fixed annual price.
    monthly: 3800,
    keywordThemes: 50,
    aiQuestions: 50,
  },
} as const;

export type SeoPlanKey = keyof typeof SEO_PLANS;

/** One-time fee for external websites on Essential. Waived on Growth and Managed. */
export const SEO_ONBOARDING_FEE = 899;

/** Free website offer: a five-page Business website with a 12-month Growth or Managed plan. */
export const SEO_FREE_WEBSITE = {
  value: 3000,
  pages: 5,
  commitmentMonths: 12,
  plans: ["growth", "managed"] as const satisfies readonly SeoPlanKey[],
} as const;

/** "RM1,999" — the site-wide way a ringgit amount is written. */
export function rm(amount: number): string {
  return `RM${amount.toLocaleString("en-MY")}`;
}

// Singapore (/singapore/ai-seo/). Approved by Jazz 2026-10-04: priced to the SG
// market, not converted from ringgit. Same position as Malaysia: Essential sits
// below the usual S$1,500-2,500 entry band, Growth at the bottom of it, Managed in
// the middle. Same terms as Malaysia: month to month, 15% off annual, free five-page
// website with a 12-month Growth or Managed plan, onboarding fee on Essential only.
export const SEO_PLANS_SGD_MONTHLY: Record<SeoPlanKey, number> = {
  essential: 690,
  growth: 1490,
  managed: 2800,
};

/** One-time fee for external websites on Essential in Singapore. */
export const SEO_ONBOARDING_FEE_SGD = 690;

/** "S$1,490" — how a Singapore dollar amount is written. */
export function sgd(amount: number): string {
  return `S$${amount.toLocaleString("en-SG")}`;
}
